// ─────────────────────────────────────────────────────────────────────────────
// WORK TYPE DEFINITIONS
// Source: Home Affairs 417 specified work
// ─────────────────────────────────────────────────────────────────────────────

export const WORK_TYPES = {
  farming:      { label: 'Plant & Animal Cultivation', icon: '🌾', description: 'Fruit/veg picking, pruning, mustering, shearing, dairy work' },
  fishing:      { label: 'Fishing & Pearling',         icon: '🎣', description: 'Commercial fishing, aquaculture, pearling' },
  forestry:     { label: 'Tree Farming & Felling',     icon: '🌲', description: 'Plantation forestry, timber felling, tree nursery' },
  mining:       { label: 'Mining',                     icon: '⛏️',  description: 'Coal, mineral and petroleum extraction; mine site ops' },
  construction: { label: 'Construction',               icon: '🏗️',  description: 'Building and infrastructure in regional areas' },
  tourism:      { label: 'Tourism & Hospitality',      icon: '🏨',  description: 'Hotels, resorts, restaurants - northern Australia only' },
  bushfire:     { label: 'Bushfire Recovery',          icon: '🔥',  description: 'Work in bushfire-declared recovery areas' },
  disaster:     { label: 'Disaster Recovery',          icon: '🌊',  description: 'Flood, cyclone and other natural disaster recovery work' },
}

export const WORK_TYPE_KEYS = Object.keys(WORK_TYPES)

// ─────────────────────────────────────────────────────────────────────────────
// STATE LOOKUP - derived from standard Australian postcode ranges
// ─────────────────────────────────────────────────────────────────────────────

export const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT']

export function postcodeToState(pc) {
  const n = parseInt(pc, 10)
  if (isNaN(n)) return null
  if (n >= 800  && n <= 999)  return 'NT'
  if (n >= 2000 && n <= 2599) return 'NSW'
  if (n >= 2600 && n <= 2618) return 'ACT'
  if (n >= 2619 && n <= 2899) return 'NSW'
  if (n >= 2900 && n <= 2920) return 'ACT'
  if (n >= 2921 && n <= 2999) return 'NSW'
  if (n >= 3000 && n <= 3999) return 'VIC'
  if (n >= 4000 && n <= 4999) return 'QLD'
  if (n >= 5000 && n <= 5999) return 'SA'
  if (n >= 6000 && n <= 6999) return 'WA'
  if (n >= 7000 && n <= 7999) return 'TAS'
  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// TIER COLOURS (used in map and legend)
// ─────────────────────────────────────────────────────────────────────────────

export const TIER_COLORS = {
  eligible: '#2e7056',  // green - eligible suburb
  partial:  '#d4900a',  // gold - eligible but work type not offered here
  none:     '#6b6560',  // grey - not eligible
}

// ─────────────────────────────────────────────────────────────────────────────
// DETAIL PANEL MAPPINGS - maps work type keys to description groups and labels
// ─────────────────────────────────────────────────────────────────────────────

// Maps each work type key to its description group in industry-descriptions.json
export const WORK_TYPE_TO_DESC_KEY = {
  farming:      'regional',
  fishing:      'regional',
  forestry:     'regional',
  mining:       'regional',
  construction: 'regional',
  tourism:      'tourism',
  bushfire:     'bushfire',
  disaster:     'disaster',
}

// Human-readable category name per regional work type
export const REGIONAL_CATEGORY_NAME = {
  farming:      'Plant and Animal Cultivation',
  fishing:      'Fishing and Pearling',
  forestry:     'Tree Farming and Felling',
  mining:       'Mining',
  construction: 'Construction',
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: get work types from an eligibility entry
// ─────────────────────────────────────────────────────────────────────────────
export function getWorkTypes(entry) {
  if (!entry) return []
  return entry.workTypes ?? []
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: compute display tier from eligibility entry + active filters
// Returns: 'eligible' | 'partial' | 'none'
//
// 'partial' = suburb IS eligible but none of the active work-type filters
// match - shown on map with faded styling.
// Works with both postcode entries { workTypes } and suburb entries { workTypes }.
// ─────────────────────────────────────────────────────────────────────────────
export function computeTier(entry, workTypeFilter, showPartial = true) {
  if (!entry) return 'none'

  const workTypes = getWorkTypes(entry)
  if (workTypes.length === 0) return 'none'

  // No work-type filter active - all eligible postcodes show as green
  if (workTypeFilter.length === 0) return 'eligible'

  // Filter active - check if this postcode offers any of the selected types
  const activeTypes = new Set(workTypeFilter)
  if (workTypes.some(t => activeTypes.has(t))) return 'eligible'
  return showPartial ? 'partial' : 'none'
}
